import { CaretDown, ListBullets, SquaresFour } from '@phosphor-icons/react';
import {
  FilterChip,
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  SegmentedControl,
  SegmentedControlItem,
} from '@reelplicate/ui';

import {
  type Facet,
  facetChipLabel,
  type FilterState,
  type SortId,
  SORTS,
} from './library';
import { useMedia } from './media-context';

export type View = 'grid' | 'list';

const ANY = '__any';

function FacetChip({
  facet,
  picked,
  onChange,
}: {
  facet: Facet;
  picked: string[];
  onChange: (picked: string[]) => void;
}) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <FilterChip dropdown selected={picked.length > 0}>
          {facetChipLabel(facet, picked)}
        </FilterChip>
      </MenuTrigger>
      <MenuContent>
        {facet.multiple ? (
          facet.options.map((o) => (
            <MenuCheckboxItem
              key={o.value}
              checked={picked.includes(o.value)}
              onCheckedChange={(on) =>
                onChange(on ? [...picked, o.value] : picked.filter((v) => v !== o.value))
              }
            >
              {o.label}
            </MenuCheckboxItem>
          ))
        ) : (
          <MenuRadioGroup
            value={picked[0] ?? ANY}
            onValueChange={(value) => onChange(value === ANY ? [] : [value])}
          >
            <MenuRadioItem value={ANY}>Any</MenuRadioItem>
            {facet.options.map((o) => (
              <MenuRadioItem key={o.value} value={o.value}>
                {o.label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        )}
      </MenuContent>
    </Menu>
  );
}

type FilterBarProps = {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  sort: SortId;
  onSortChange: (sort: SortId) => void;
  /** A search is on: Relevance joins the sort options. */
  searching: boolean;
  view: View;
  onViewChange: (view: View) => void;
};

/**
 * Figma "Filters" (185:320): a Filter Chip per facet (each opens a Menu),
 * Clear all once something is picked, then Sort and the grid / list switch.
 */
export function FilterBar({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  searching,
  view,
  onViewChange,
}: FilterBarProps) {
  const { facets } = useMedia();
  const active = Object.values(filters).some((v) => v.length > 0);
  const sorts = SORTS.filter((s) => searching || s.value !== 'relevance');
  const current = sorts.find((s) => s.value === sort) ?? sorts[0]!;

  return (
    <div className="flex min-h-[100px] flex-wrap items-center gap-2">
      {facets.map((facet) => (
        <FacetChip
          key={facet.id}
          facet={facet}
          picked={filters[facet.id] ?? []}
          onChange={(picked) => onFiltersChange({ ...filters, [facet.id]: picked })}
        />
      ))}
      {active && (
        <button
          type="button"
          onClick={() => onFiltersChange({})}
          className="text-body-sm-strong text-text-secondary focus-visible:shadow-focus-ring rounded-xs px-0.5 outline-none hover:underline"
        >
          Clear all
        </button>
      )}
      <span className="flex-1" />
      <Menu>
        <MenuTrigger className="text-caption focus-visible:shadow-focus-ring flex items-center gap-1.5 rounded-xs outline-none">
          <span className="text-text-tertiary">Sort</span>
          <span className="text-label-sm text-text-primary">{current.label}</span>
          <CaretDown aria-hidden className="text-icon-secondary size-3.5" />
        </MenuTrigger>
        <MenuContent align="end">
          <MenuRadioGroup
            value={current.value}
            onValueChange={(v) => onSortChange(v as SortId)}
          >
            {sorts.map((s) => (
              <MenuRadioItem key={s.value} value={s.value}>
                {s.label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuContent>
      </Menu>
      <SegmentedControl
        aria-label="View"
        value={view}
        onValueChange={(v) => onViewChange(v as View)}
        className="gap-0.5 rounded-lg p-[3px]"
      >
        <SegmentedControlItem value="grid" className="rounded-sm p-[5px]">
          <SquaresFour aria-hidden className="size-4" />
          <span className="sr-only">Grid</span>
        </SegmentedControlItem>
        <SegmentedControlItem value="list" className="rounded-sm p-[5px]">
          <ListBullets aria-hidden className="size-4" />
          <span className="sr-only">List</span>
        </SegmentedControlItem>
      </SegmentedControl>
    </div>
  );
}
